import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigationEvents } from '@/lib/navigation-events';
import type { CartItem, OrderHistoryItem } from '@/types';
import { logger } from '@/utils/logger';
import { ROUTES } from '@/config/routes';

interface OrderCreationData {
  orderData: {
    restaurant?: string;
    grossSubtotal?: number;
    netSubtotal?: number;
    tax?: number;
    tip?: number;
    total?: number;
    paymentMethod?: string;
  } | null;
  cartItems: CartItem[];
  orderedItems: CartItem[];
  restaurantName: string;
  isTableOrder: boolean;
  tableNumber: string | null;
}

interface UseOrderCompletionProps {
  // 주문 데이터
  selectedRestaurant: { nameKey: string } | null;
  orderData: {
    restaurant?: string;
    grossSubtotal?: number;
    netSubtotal?: number;
    tax?: number;
    tip?: number;
    total?: number;
    paymentMethod?: string;
  } | null;
  cartItems: CartItem[];
  orderedItems: CartItem[];
  showTableOrderMenu: boolean;

  // 번역
  getRestaurantName: (nameKey: string) => string;

  // 주문 생성
  createOrderFromCheckout: (data: OrderCreationData) => OrderHistoryItem | null;

  // 상태 초기화 함수들
  setCartItems: (items: never[]) => void;
  resetPaymentState: () => void;
  resetTableOrderState: () => void;
  resetRestaurantState: () => void;
  setActiveTab: (tab: string) => void;
}

/**
 * 주문 완료 처리 훅
 * 영수증 닫기 시 모든 상태를 초기화하고 주문 내역에 추가
 */
export function useOrderCompletion({
  selectedRestaurant,
  orderData,
  cartItems,
  orderedItems,
  showTableOrderMenu,
  getRestaurantName,
  createOrderFromCheckout,
  setCartItems,
  resetPaymentState,
  resetTableOrderState,
  resetRestaurantState,
  setActiveTab,
}: UseOrderCompletionProps) {
  // #475 R3 P1: 상태 초기화 후 명시 redirect — /receipt/:id 잔류 차단 (silent fallback 금지)
  const navigate = useNavigate();

  return useCallback(() => {
    // 주문 내역에 새 주문 추가
    const restaurantName = selectedRestaurant?.nameKey
      ? getRestaurantName(selectedRestaurant.nameKey)
      : '';

    createOrderFromCheckout({
      orderData,
      cartItems,
      orderedItems,
      restaurantName,
      isTableOrder: showTableOrderMenu,
      // (#414) 이 레거시 경로의 tableNumber 는 항상 null 이었음 (App.tsx currentTable
      // dead state — 어디서도 set 안 됨). 동작 보존을 위해 null 유지.
      // 실제 테이블 주문 스냅샷은 CheckoutPage 가 sessionStorage SSOT
      // (getTableSessionFromStorage) 에서 tableNumber 를 파생해 직접 생성한다.
      tableNumber: null,
    });

    // 상태 초기화
    setCartItems([]);
    navigationEvents.closeCart();
    resetPaymentState();
    resetTableOrderState();
    resetRestaurantState();
    setActiveTab('home');

    // #475 R3 P1: /receipt/:id 잔류 차단 — 홈으로 명시 redirect (replace 로 history 오염 방지)
    navigate(ROUTES.HOME, { replace: true });

    logger.info('주문이 완료되었습니다. 새로운 주문을 시작할 수 있습니다.');
  }, [
    selectedRestaurant,
    orderData,
    cartItems,
    orderedItems,
    showTableOrderMenu,
    getRestaurantName,
    createOrderFromCheckout,
    setCartItems,
    resetPaymentState,
    resetTableOrderState,
    resetRestaurantState,
    setActiveTab,
    navigate,
  ]);
}
